import { Button } from "../../primitives/button";
import { Badge } from "../../primitives/badge";
import { PriorityBadge } from "./PriorityBadge";
import { SegmentBadge } from "../customers/SegmentBadge";
import { X, Lightbulb, ExternalLink } from "lucide-react";
import type * as API from "@shared-core/data/api-types";
import {
  formatDateOrDash,
  formatDateTimeOrDash,
} from "@shared-core/domain/formatters";
import { Link } from "react-router";
import { isRouteEnabled, paths } from "@shared-core/routing/routes";

interface RecommendationDetailModalProps {
  recommendation: API.Opportunity;
  onClose: () => void;
  onNotRelevant?: () => void;
  onCreateTicket?: () => void;
  onViewTicket?: (ticketId: string) => void;
}

const STATUS_LABEL: Record<string, string> = {
  open: "Open",
  done: "Completed",
  dismissed: "Dismissed",
  converted_to_task: "Converted to task",
};

export function RecommendationDetailModal({
  recommendation,
  onClose,
  onNotRelevant,
  onCreateTicket,
  onViewTicket,
}: RecommendationDetailModalProps) {
  const customer = recommendation.customer ?? null;
  const hasTicket = !!recommendation.converted_to_task_id;
  const isInactive = recommendation.status !== "open";
  const dueDate = recommendation.due_date
    ? new Date(recommendation.due_date)
    : null;

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-background border border-border rounded-lg shadow-lg w-full max-w-2xl max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="shrink-0 bg-background border-b border-border px-6 py-4">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-3 flex-1 min-w-0">
              <Lightbulb className="size-6 text-primary shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <div className="text-xs uppercase tracking-wide text-muted-foreground mb-0.5">
                  Recommendation
                </div>
                {customer ? (
                  <Link
                    to={paths.customerDetail({ customerId: customer.id })}
                    onClick={onClose}
                    className="text-lg font-medium hover:underline text-[color:var(--color-primary)] inline-flex items-center gap-1"
                  >
                    {customer.name}
                    <ExternalLink className="size-3.5" />
                  </Link>
                ) : (
                  <div className="text-lg font-medium">Unknown customer</div>
                )}
                <div className="flex items-center gap-2 flex-wrap mt-2">
                  <PriorityBadge priority={recommendation.priority} />
                  {recommendation.segment_id && (
                    <SegmentBadge segment={recommendation.segment_id} />
                  )}
                  <Badge variant="outline" className="text-xs">
                    {STATUS_LABEL[recommendation.status] ??
                      recommendation.status}
                  </Badge>
                </div>
              </div>
            </div>
            <Button variant="ghost" size="icon" onClick={onClose}>
              <X className="size-4" />
            </Button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 min-h-0 overflow-y-auto p-6 space-y-5">
          <div>
            <h3 className="text-base font-medium mb-1">
              {recommendation.title}
            </h3>
            {dueDate && (
              <p className="text-sm text-muted-foreground">
                Due {formatDateOrDash(dueDate)}
              </p>
            )}
          </div>

          {recommendation.rationale && (
            <div>
              <h4 className="text-sm font-medium mb-1">
                Why we're suggesting this
              </h4>
              <p className="text-sm text-muted-foreground">
                {recommendation.rationale}
              </p>
            </div>
          )}

          {recommendation.suggested_message && (
            <div>
              <h4 className="text-sm font-medium mb-1">Suggested message</h4>
              <div className="p-3 border rounded-lg bg-muted/30">
                <p className="text-sm italic">
                  {recommendation.suggested_message}
                </p>
              </div>
            </div>
          )}

          <div className="border-t pt-4">
            <h4 className="text-sm font-medium mb-3">Why this fired</h4>

            {recommendation.signal_commentary && (
              <div className="mb-3 p-3 border-l-2 border-[color:var(--color-primary)] bg-muted/30 rounded-sm">
                <p className="text-sm">{recommendation.signal_commentary}</p>
              </div>
            )}

            <dl className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-2 text-sm">
              <dt className="text-muted-foreground">Family</dt>
              <dd className="capitalize">
                {recommendation.opportunity_family}
                {recommendation.suggested_action_type && (
                  <>
                    {" · "}
                    <span className="capitalize">
                      {recommendation.suggested_action_type}
                    </span>
                  </>
                )}
              </dd>

              {recommendation.opportunity_definition_id && (
                <>
                  <dt className="text-muted-foreground">Recommendation rule</dt>
                  <dd>
                    {/* Definition editors are `recommendations_config_edit`,
                        held back from production. Keep the rule's name; drop
                        the link into an editor that is not there. */}
                    {isRouteEnabled("settingsRecommendationEdit") ? (
                      <Link
                        to={paths.settingsRecommendationEdit({
                          id: recommendation.opportunity_definition_id,
                        })}
                        onClick={onClose}
                        className="hover:underline text-[color:var(--color-primary)] inline-flex items-center gap-1"
                      >
                        {recommendation.opportunity_name}
                        <ExternalLink className="size-3" />
                      </Link>
                    ) : (
                      recommendation.opportunity_name
                    )}
                  </dd>
                </>
              )}

              {recommendation.signal_definition_id && (
                <>
                  <dt className="text-muted-foreground">Driving signal</dt>
                  <dd>
                    {isRouteEnabled("settingsSignalEdit") ? (
                      <Link
                        to={paths.settingsSignalEdit({
                          id: recommendation.signal_definition_id,
                        })}
                        onClick={onClose}
                        className="hover:underline text-[color:var(--color-primary)] inline-flex items-center gap-1"
                      >
                        {recommendation.signal_name ?? "View signal definition"}
                        <ExternalLink className="size-3" />
                      </Link>
                    ) : (
                      recommendation.signal_name
                    )}
                    {recommendation.signal_value && (
                      <span className="text-muted-foreground ml-1.5">
                        · {recommendation.signal_value}
                      </span>
                    )}
                    {recommendation.signal_impact &&
                      recommendation.signal_impact !== "neutral" && (
                        <Badge
                          variant="outline"
                          className="text-xs ml-2 capitalize"
                        >
                          {recommendation.signal_impact}
                        </Badge>
                      )}
                  </dd>
                </>
              )}

              <dt className="text-muted-foreground">Created</dt>
              <dd>{formatDateTimeOrDash(recommendation.created_at)}</dd>

              {recommendation.completed_at && (
                <>
                  <dt className="text-muted-foreground">Completed</dt>
                  <dd>{formatDateTimeOrDash(recommendation.completed_at)}</dd>
                </>
              )}
            </dl>
          </div>
        </div>

        {/* Footer */}
        <div className="shrink-0 bg-background border-t border-border px-6 py-4 flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>
            Close
          </Button>
          {!isInactive && !hasTicket && onNotRelevant && (
            <Button variant="outline" onClick={onNotRelevant}>
              Dismiss
            </Button>
          )}
          {hasTicket && onViewTicket ? (
            <Button
              onClick={() => onViewTicket(recommendation.converted_to_task_id!)}
            >
              <ExternalLink className="size-4" />
              View ticket
            </Button>
          ) : (
            !isInactive &&
            onCreateTicket && (
              <Button onClick={onCreateTicket}>Convert to ticket</Button>
            )
          )}
        </div>
      </div>
    </div>
  );
}
